import {
  ContentType,
  DayOfWeek,
  LucideIconName,
  OpeningHoursData,
} from '@bm-js/atlascloud-shared'
import { _stringEnumValues } from '@naturalcycles/js-lib'
import { LocalTime, localTime } from '@naturalcycles/js-lib/datetime'
import {
  _objectEntries,
  IsoDate,
  IsoDateTime,
} from '@naturalcycles/js-lib/types'
import { useCallback, useMemo, useState } from 'react'
import { useMargin } from '../utils/hooks'
import { CustomerButton } from './CustomerButton'
import { Icon } from './Icon'

export const OpeningHoursContent = ({ content }: OpeningHoursProps) => {
  const [accordionOpen, setAccordionOpen] = useState(false)

  const margins = useMargin(content)

  const getOpeningHoursAndWeekdayForDate = useCallback(
    (date: LocalTime) => {
      const dayOfWeek = _stringEnumValues(DayOfWeek)[date.dayOfWeek - 1]
      return {
        openingHours: content.data.defaultOpeningHours[dayOfWeek],
        dayOfWeek,
      }
    },
    [content.data.defaultOpeningHours]
  )

  const todayOpenHours = useMemo(() => {
    return getOpeningHoursAndWeekdayForDate(localTime.now()).openingHours
  }, [getOpeningHoursAndWeekdayForDate])

  const nextOpenMoment = useMemo(() => {
    const now = localTime.now()
    const maxDaysToCheck = 7
    let currentCheck = 1
    while (currentCheck < maxDaysToCheck) {
      const currentDate = localTime(now).plusDays(currentCheck)
      const openHoursAndWeekday = getOpeningHoursAndWeekdayForDate(currentDate)
      if (openHoursAndWeekday.openingHours) return openHoursAndWeekday

      currentCheck++
    }
    return null
  }, [getOpeningHoursAndWeekdayForDate])

  const isOpenNow = useMemo(() => {
    if (!todayOpenHours) return false
    const openTime = hourFractionToLocalTime(todayOpenHours.openHour)
    const closeTime = hourFractionToLocalTime(todayOpenHours.closeHour)
    return getNowWithArbitraryDate().isBetween(openTime, closeTime, '[)')
  }, [todayOpenHours])

  const closesAt = useMemo(() => {
    if (!todayOpenHours || !isOpenNow) return null
    const todayCloseTime = hourFractionToLocalTime(todayOpenHours.closeHour)
    return localTimeToTimeString(todayCloseTime)
  }, [isOpenNow, todayOpenHours])

  const opensAt = useMemo(() => {
    if (isOpenNow) return null
    const willOpenToday =
      todayOpenHours &&
      getNowWithArbitraryDate().isBefore(
        hourFractionToLocalTime(todayOpenHours.openHour)
      )

    if (willOpenToday) {
      return localTimeToTimeString(
        hourFractionToLocalTime(todayOpenHours.openHour)
      )
    }

    if (nextOpenMoment?.openingHours) {
      return `${localTimeToTimeString(
        hourFractionToLocalTime(nextOpenMoment.openingHours.openHour)
      )} på ${weekdayNames[nextOpenMoment.dayOfWeek]}`
    }
    return null
  }, [todayOpenHours, isOpenNow, nextOpenMoment])

  return (
    <div
      className={`border border-gray-200 rounded-md p-4 max-w-sm ${margins}`}
    >
      <h2 className='font-bold text-xl'>
        {isOpenNow ? 'Öppet nu' : 'Stängt nu'}
      </h2>
      {!!(isOpenNow && closesAt) && <p>Stänger klockan {closesAt}</p>}
      {!!(!isOpenNow && opensAt) && <p>Öppnar klockan {opensAt}</p>}

      <div
        className={`overflow-hidden max-h-0 transition-all ${
          accordionOpen ? 'max-h-[400px] mt-4' : ''
        }`}
      >
        {_objectEntries(content.data.defaultOpeningHours).map(
          ([day, openingHours]) => (
            <div
              className='flex items-center justify-between border-b border-gray-200 pb-1 mb-2'
              key={`${day}-opening-hours`}
            >
              <p>{weekdayNames[day]}</p>
              <p>
                {openingHours
                  ? `${localTimeToTimeString(
                      hourFractionToLocalTime(openingHours.openHour)
                    )} - ${localTimeToTimeString(
                      hourFractionToLocalTime(openingHours.closeHour)
                    )}`
                  : 'Stängt'}
              </p>
            </div>
          )
        )}
      </div>

      <CustomerButton
        onClick={() => setAccordionOpen((v) => !v)}
        btnType='outline'
        className='flex items-center gap-1 mt-4'
      >
        {accordionOpen ? 'Visa färre öppettider' : 'Visa alla öppettider'}
        <Icon
          size={20}
          name={
            accordionOpen
              ? LucideIconName.ChevronUp
              : LucideIconName.ChevronDown
          }
          className='mt-0.5'
        />
      </CustomerButton>
    </div>
  )
}

const weekdayNames: Record<DayOfWeek, string> = {
  [DayOfWeek.Monday]: 'Måndag',
  [DayOfWeek.Tuesday]: 'Tisdag',
  [DayOfWeek.Wednesday]: 'Onsdag',
  [DayOfWeek.Thursday]: 'Torsdag',
  [DayOfWeek.Friday]: 'Fredag',
  [DayOfWeek.Saturday]: 'Lördag',
  [DayOfWeek.Sunday]: 'Söndag',
}

const ARBITRARY_DATE = '1970-01-01' as IsoDate

function getNowWithArbitraryDate(): LocalTime {
  const now = localTime.now()
  const hour = now.hour.toString().padStart(2, '0')
  const minute = now.minute.toString().padStart(2, '0')
  const second = now.second.toString().padStart(2, '0')
  return localTime.fromInput(
    `${ARBITRARY_DATE}T${hour}:${minute}:${second}` as IsoDateTime
  )
}

function hourFractionToLocalTime(fraction: number): LocalTime {
  const hour = Math.floor(fraction)
  const hourString = hour.toString().padStart(2, '0')
  const minute = Math.round((fraction - hour) * 60)
  const minuteString = minute.toString().padStart(2, '0')
  return localTime.fromInput(
    `${ARBITRARY_DATE}T${hourString}:${minuteString}:00` as IsoDateTime
  )
}

function localTimeToTimeString(time: LocalTime): string {
  const hour = time.hour.toString().padStart(2, '0')
  const minute = time.minute.toString().padStart(2, '0')
  return `${hour}:${minute}`
}

interface OpeningHoursProps {
  content: ContentType<OpeningHoursData>
}
